import { useCallback, useId, useMemo, useRef } from 'react';
import { FollowSelector } from '../components/FollowSelector';
import { useFollowSelectorContext } from '../context/FollowSelectorContext';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { isUserLoggedIn, useUser } from '@modules/user';
import type { PinItemType } from '~/modules/moodboards/type';

interface UseFollowSelectorProxyArgs {
    pinId: number;
    pinType: PinItemType;
    libraryItemId?: number;
    onFollowChange?: (isFollowed: boolean) => void;
}

/**
 * Find the nearest ancestor that creates a new containing block for position:fixed.
 * CSS transforms, perspective, and filter on ancestors cause position:fixed to be
 * relative to that ancestor instead of the viewport (e.g. inside Swiper carousels).
 */
const findFixedContainingBlock = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;
    while (parent && parent !== document.documentElement) {
        const { transform, perspective, filter } = getComputedStyle(parent);
        if (
            transform !== 'none' ||
            perspective !== 'none' ||
            (filter !== '' && filter !== 'none')
        ) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};

/**
 * Provides a programmatic way to open the FollowSelector while preserving existing button UI.
 * Useful when the trigger is rendered internally by Aria components and cannot be wrapped.
 *
 * Opens the selector by calling the context directly (no hidden button proxy).
 * Positions the popover at the clicked element by imperatively setting the anchor's
 * fixed position to match the target's bounding rect.
 *
 * Compensates for CSS transform containers (e.g. Swiper carousels) that create
 * a new containing block for position:fixed elements.
 *
 * Pass a React.MouseEvent to `openFollowSelector(e)` for accurate positioning.
 */
export const useFollowSelectorProxy = ({
    pinId,
    pinType,
    libraryItemId,
    onFollowChange,
}: UseFollowSelectorProxyArgs) => {
    const instanceId = useId();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { openFollowSelector: contextOpen } = useFollowSelectorContext();
    const { guestSave } = useGuestSave();
    const anchorRef = useRef<HTMLSpanElement>(null);

    const selectedItemIds = useMemo(
        () => [libraryItemId ?? pinId],
        [libraryItemId, pinId],
    );

    /**
     * Position the anchor span at the target element's viewport location.
     * Compensates for CSS transform containers (e.g. Swiper) that make
     * position:fixed relative to the transformed ancestor instead of the viewport.
     */
    const positionAnchorAt = useCallback((element: HTMLElement) => {
        if (!anchorRef.current) return;
        const targetRect = element.getBoundingClientRect();
        const style = anchorRef.current.style;

        // CSS transforms on ancestors create a new containing block for
        // position:fixed, making coordinates relative to the transformed
        // ancestor instead of the viewport. Subtract its position to compensate.
        const containingBlock = findFixedContainingBlock(anchorRef.current);
        const cbRect = containingBlock?.getBoundingClientRect();
        const offsetX = cbRect?.left ?? 0;
        const offsetY = cbRect?.top ?? 0;

        style.top = `${targetRect.top - offsetY}px`;
        style.left = `${targetRect.left - offsetX}px`;
        style.width = `${targetRect.width}px`;
        style.height = `${targetRect.height}px`;
    }, []);

    /**
     * Resolve the best DOM element to anchor the popover to.
     * Prefers the closest <button> ancestor of the actual click target (the heart icon),
     * falling back to currentTarget or the target itself.
     */
    const resolveAnchorTarget = useCallback(
        (
            eventOrElement: React.SyntheticEvent<HTMLElement> | HTMLElement,
        ): HTMLElement | null => {
            if (eventOrElement instanceof HTMLElement) {
                return eventOrElement;
            }

            // Use the actual clicked element and walk up to the nearest button
            // for accurate positioning (e.currentTarget can be a large wrapper).
            const clicked = eventOrElement.target as HTMLElement | null;
            return (
                clicked?.closest<HTMLElement>('button') ??
                (eventOrElement.currentTarget as HTMLElement | null) ??
                clicked
            );
        },
        [],
    );

    /**
     * Open the follow selector popover.
     * Pass a MouseEvent or HTMLElement for accurate positioning next to the trigger.
     * For logged-out users, shows the auth modal so they can log in first.
     */
    const openFollowSelector = useCallback(
        (eventOrElement?: React.SyntheticEvent<HTMLElement> | HTMLElement) => {
            // Guest flow: store pin and show auth modal
            if (!isLoggedIn) {
                guestSave({ pinId, pinType, libraryItemId });
                return;
            }

            // Always open the selector first — positioning is best-effort
            contextOpen(instanceId, {
                selectedItemIds,
                selectedItemType: pinType,
            });

            if (eventOrElement) {
                const target = resolveAnchorTarget(eventOrElement);
                if (target) {
                    positionAnchorAt(target);
                }
            }
        },
        [
            isLoggedIn,
            guestSave,
            pinId,
            pinType,
            libraryItemId,
            contextOpen,
            instanceId,
            selectedItemIds,
            positionAnchorAt,
            resolveAnchorTarget,
        ],
    );

    const followSelector = useMemo(
        () => (
            <FollowSelector
                instanceId={instanceId}
                anchorRef={anchorRef}
                pinId={pinId}
                libraryItemId={libraryItemId}
                pinType={pinType}
                onFollowChange={onFollowChange}
            />
        ),
        [instanceId, libraryItemId, onFollowChange, pinId, pinType],
    );

    return { followSelector, openFollowSelector };
};
